import { ICommand } from '../../../core/commands/ICommand';
import { flowchartStyleInheritance } from '../services/flowchartStyleInheritance';
import { CreateShapeCommand } from '../../../core/commands/CreateShapeCommand';
import { getShapeManifest } from '../../../lib/diagram/registries';
import { getNodeMutationManager } from '../../../core/commands/mutations/NodeMutationManager';
import { MoveNodesCommand } from '../../genogram/tools/GenogramTransformCommands';
import { ProcessLayoutService } from '../services/ProcessLayoutService';
import { getConnectedFlowchartNodeIds } from '../services/processGraph.utils';
import { useDiagramStore } from '../../../stores/diagramStore';
import { useFlowchartLayoutStore } from '../../../stores/flowchartLayoutStore';
import { CreateDecisionCommand } from './CreateDecisionCommand';
import { CreateProcessStepCommand } from './CreateProcessStepCommand';
import { CreateSequenceFlowCommand } from './CreateSequenceFlowCommand';
import { CreateTerminatorCommand } from './CreateTerminatorCommand';
import { UpdateEdgeCommand } from '../../../core/commands/edges/UpdateEdgeCommand';
import { getTranslatedFlowchartShapeLabel } from '../shapes/flowchart/flowchartShapeI18n';
import { anchorSideFromPoint, chooseAnchorPairForStrategy, rectFromNode } from '../services/flowchartAnchors';
import { planQuickCreatePlacement } from '../../../core/layout/spatialIntent';
import { getDefaultDecisionBranchLabel } from '../services/processDecisionBranchLabels';
import {
  createNodeId,
  createEdgeId,
  type NodeAddition,
  type EdgeAddition,
} from '../../../core/data-profiles/scenarios/ScenarioDelta';
import type { CommandDeltaContribution, IScenarioRecordable } from '../../../core/data-profiles/scenarios/ScenarioRecordable';

export interface QuickCreateFlowchartNodeParams {
  sourceNodeId: string;
  variant: string;
  direction: 'top' | 'right' | 'bottom' | 'left';
  sourceX: number;
  sourceY: number;
  sourceW: number;
  sourceH: number;
  dataSourceId?: string;
  parentRecordId?: string;
}

/**
 * Command to quickly create a flowchart node connected to an existing node.
 * 
 * This command atomically creates:
 * 1. A new flowchart shape (process, decision, terminator, etc.)
 * 2. A sequence_flow connector from the source node to the new node
 * 
 * The new node is positioned based on the direction (top, right, bottom, left)
 * with appropriate spacing and anchor points.
 */
export class QuickCreateFlowchartNodeCommand implements ICommand, IScenarioRecordable {
  id: string;
  name: string;
  description?: string;
  timestamp: number;
  readonly isScenarioRecordable = true as const;

  private params: QuickCreateFlowchartNodeParams;
  private shapeCmd: ICommand & { getCreatedId?: () => string | undefined };
  private edgeCmd?: ICommand;
  private readonly inheritedStyle: Record<string, unknown>;
  private createdShapeId?: string;
  private moveCmd?: MoveNodesCommand;
  private edgeUpdateCommands: UpdateEdgeCommand[] = [];
  private readonly placementDirection: 'top' | 'right' | 'bottom' | 'left';
  private readonly branchLabel?: 'Yes' | 'No';
  private readonly manualPlacementMoves: Array<{
    nodeId: string;
    oldPosition: { x: number; y: number };
    newPosition: { x: number; y: number };
  }>;
  private scenarioNodeId: ReturnType<typeof createNodeId>;
  private scenarioAddition: NodeAddition;
  private scenarioEdgeId: ReturnType<typeof createEdgeId>;
  private scenarioEdgeAddition: EdgeAddition;

  constructor(params: QuickCreateFlowchartNodeParams) {
    this.id = `quick-create-flowchart-${Date.now()}`;
    this.name = 'Quick Create Flowchart Node';
    this.description = `Create ${params.variant} and connect with sequence flow`;
    this.timestamp = Date.now();
    this.params = params;
    this.scenarioNodeId = createNodeId(`scenario-${this.id}`);
    this.scenarioEdgeId = createEdgeId(`scenario-${this.id}-edge`);

    const { variant, direction, sourceX, sourceY, sourceW, sourceH } = params;

    // Calculate new shape position based on direction
    const gap = 40; // Spacing between shapes
    // Get default dimensions from manifest
    const dimsVariant = (() => {
      switch (variant) {
        case 'data':
          return 'input-output';
        default:
          return variant;
      }
    })();
    const manifest = getShapeManifest('flowchart', dimsVariant);
    const defaultDims = manifest?.defaultDimensions || { width: 120, height: 80 };
    const newW = defaultDims.width;
    const newH = defaultDims.height;

    const store = useDiagramStore.getState();
    this.inheritedStyle = flowchartStyleInheritance(store.nodes.find(node => node.id === params.sourceNodeId));
    this.branchLabel = getDefaultDecisionBranchLabel(params.sourceNodeId, store.nodes, store.edges);
    const placement = planQuickCreatePlacement({
      sourceNodeId: params.sourceNodeId,
      nodes: store.nodes,
      edges: store.edges,
      requestedDirection: direction,
      newNodeSize: { width: newW, height: newH },
      sourceFallbackRect: { x: sourceX, y: sourceY, width: sourceW, height: sourceH },
      gap,
    });
    const newX = placement.position.x;
    const newY = placement.position.y;
    this.placementDirection = placement.direction;
    this.manualPlacementMoves = placement.moves;

    const defaultLabel = getTranslatedFlowchartShapeLabel(variant);
    const scenarioType = variant;
    const scenarioProps: Record<string, unknown> = (() => {
      switch (variant) {
        case 'process':
          return { description: '', activityType: 'task' };
        case 'decision':
          return { description: '', gatewayType: 'exclusive' };
        case 'start-end':
          return { description: '', terminatorType: 'start' };
        default:
          return {};
      }
    })();

    this.scenarioAddition = {
      id: this.scenarioNodeId,
      type: scenarioType,
      name: defaultLabel,
      position: { x: newX, y: newY },
      props: { ...scenarioProps, ...this.inheritedStyle },
    };

    const anchorsForScenario = (
      dir: 'top' | 'right' | 'bottom' | 'left'
    ): { from: 'top' | 'right' | 'bottom' | 'left'; to: 'top' | 'right' | 'bottom' | 'left' } => {
      switch (dir) {
        case 'top':
          return { from: 'top', to: 'bottom' };
        case 'right':
          return { from: 'right', to: 'left' };
        case 'bottom':
          return { from: 'bottom', to: 'top' };
        case 'left':
          return { from: 'left', to: 'right' };
      }
    };

    const scenarioAnchors = anchorsForScenario(this.placementDirection);
    this.scenarioEdgeAddition = {
      id: this.scenarioEdgeId,
      type: 'sequence_flow',
      from: createNodeId(this.params.sourceNodeId),
      to: this.scenarioNodeId,
      props: {
        fromAnchor: scenarioAnchors.from,
        toAnchor: scenarioAnchors.to,
        fromOffset: 0.5,
        toOffset: 0.5,
        routingType: 'orthogonalRounded',
        ...(this.branchLabel ? { label: this.branchLabel } : {}),
      },
    };

    if (variant === 'process') {
      this.shapeCmd = new CreateProcessStepCommand({
        initialStyle: this.inheritedStyle,
        x: newX,
        y: newY,
        width: newW,
        height: newH,
        label: defaultLabel,
        dataSourceId: params.dataSourceId,
        parentRecordId: params.parentRecordId,
      });
    } else if (variant === 'decision') {
      this.shapeCmd = new CreateDecisionCommand({
        initialStyle: this.inheritedStyle,
        x: newX,
        y: newY,
        width: newW,
        height: newH,
        label: defaultLabel,
      });
    } else if (variant === 'start-end') {
      this.shapeCmd = new CreateTerminatorCommand({
        initialStyle: this.inheritedStyle,
        x: newX,
        y: newY,
        width: newW,
        height: newH,
        label: defaultLabel,
      });
    } else {
      const label = defaultLabel;
      this.shapeCmd = new CreateShapeCommand({
        initialStyle: this.inheritedStyle,
        type: variant,
        category: 'flowchart',
        x: newX,
        y: newY,
        width: newW,
        height: newH,
        text: label,
      }) as any;
    }
  }

  async execute(): Promise<void> {
    const storeBefore = useDiagramStore.getState();
    const preFlowchartNodesCount = storeBefore.nodes.filter((n: any) =>
      n?.type === 'process' || n?.type === 'decision' || n?.type === 'terminator' || n?.type === 'data' || n?.type === 'document' || n?.type === 'flowchart-shape'
    ).length;

    const mm = getNodeMutationManager();
    mm.runInTransaction(() => {
      this.shapeCmd.execute();
      this.createdShapeId = (this.shapeCmd as any)?.getCreatedId?.();
      if (!this.createdShapeId) {
        throw new Error('Failed to create flowchart shape: no ID returned');
      }

      const anchorsFor = (dir: 'top' | 'right' | 'bottom' | 'left'): { from: 'top' | 'right' | 'bottom' | 'left'; to: 'top' | 'right' | 'bottom' | 'left' } => {
        switch (dir) {
          case 'top': return { from: 'top', to: 'bottom' };
          case 'right': return { from: 'right', to: 'left' };
          case 'bottom': return { from: 'bottom', to: 'top' };
          case 'left': return { from: 'left', to: 'right' };
        }
      };

      const { from, to } = anchorsFor(this.placementDirection);

      this.edgeCmd = new CreateSequenceFlowCommand({
        from: this.params.sourceNodeId,
        to: this.createdShapeId!,
        fromAnchor: from,
        toAnchor: to,
        routingType: 'orthogonalRounded',
        label: this.branchLabel,
      });
      this.edgeCmd.execute();
    });

    // Check auto-layout mode from store
    const layoutStore = useFlowchartLayoutStore.getState();
    const layoutStrategy = layoutStore.layoutStrategy;

    const waitForSync = async () => {
      await new Promise<void>((resolve) => {
        if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(() => resolve());
        } else {
          setTimeout(() => resolve(), 0);
        }
      });
    };

    // CreateShapeCommand schedules an async dock + reflow when the new
    // shape lands inside a swimlane/table cell, which moves it after
    // we've pinned the connector. Wait out that RAF chain and refresh
    // the edge anchors against the shape's *current* position so the
    // visible anchor handle doesn't end up disconnected from the
    // shape. Runs unconditionally — auto-layout mode only gates the
    // separate ELK relayout below.
    await waitForSync();
    await waitForSync();
    await waitForSync();
    this.refreshEdgeAnchors(layoutStrategy);

    if (layoutStore.autoLayoutMode === 'off') {
      if (this.manualPlacementMoves.length > 0) {
        this.moveCmd = new MoveNodesCommand(this.manualPlacementMoves);
        await this.moveCmd.execute();
        this.refreshEdgeAnchors(layoutStrategy);
      }
      return;
    }

    if (preFlowchartNodesCount <= 1) {
      await waitForSync();
    }
    await waitForSync();

    const store = useDiagramStore.getState();

    const layoutService = new ProcessLayoutService();
    const layoutOptions = {
      strategy: layoutStore.layoutStrategy,
      nodeSpacing: 80,
      rankSpacing: 100,
    };

    let layout: { nodePositions: Map<string, { x: number; y: number }>; edgeEndpoints: Map<string, { start: { x: number; y: number }; end: { x: number; y: number } }> } = {
      nodePositions: new Map(),
      edgeEndpoints: new Map(),
    };
    if (layoutStore.autoLayoutMode === 'full') {
      const seedIds = this.createdShapeId
        ? [this.params.sourceNodeId, this.createdShapeId]
        : [this.params.sourceNodeId];
      const connectedNodeIds = getConnectedFlowchartNodeIds(store.nodes, store.edges, seedIds);
      layout = await layoutService.incrementalLayout(store.nodes, store.edges, connectedNodeIds, layoutOptions);
    } else {
      // Incremental layout: layout affected nodes (source + newly created)
      // The incrementalLayout will also include any nodes connected to these via edges
      const affectedNodeIds = this.createdShapeId
        ? [this.params.sourceNodeId, this.createdShapeId]
        : [this.params.sourceNodeId];
      layout = await layoutService.incrementalLayout(store.nodes, store.edges, affectedNodeIds, layoutOptions);
    }

    // If layout returned empty results, try full layout as fallback
    if (layout.nodePositions.size === 0) {
      const seedIds = this.createdShapeId
        ? [this.params.sourceNodeId, this.createdShapeId]
        : [this.params.sourceNodeId];
      const connectedNodeIds = getConnectedFlowchartNodeIds(store.nodes, store.edges, seedIds);
      layout = await layoutService.incrementalLayout(store.nodes, store.edges, connectedNodeIds, layoutOptions);
    }

    if (layout.nodePositions.size > 0) {

      const sourceBefore = storeBefore.nodes.find((n) => n.id === this.params.sourceNodeId);
      const sourceLayout = layout.nodePositions.get(this.params.sourceNodeId);
      const dx = sourceBefore && sourceLayout ? (sourceBefore.x ?? 0) - sourceLayout.x : 0;
      const dy = sourceBefore && sourceLayout ? (sourceBefore.y ?? 0) - sourceLayout.y : 0;

      const shiftedEdgeEndpoints = new Map<string, { start: { x: number; y: number }; end: { x: number; y: number } }>();
      for (const [edgeId, pts] of layout.edgeEndpoints.entries()) {
        shiftedEdgeEndpoints.set(edgeId, {
          start: { x: pts.start.x + dx, y: pts.start.y + dy },
          end: { x: pts.end.x + dx, y: pts.end.y + dy },
        });
      }

      const epsilon = 0.01;
      const moves = Array.from(layout.nodePositions.entries())
        .map(([nodeId, pos]) => {
          const n = store.nodes.find(nn => nn.id === nodeId);
          if (!n) return null;
          const anchored = { x: pos.x + dx, y: pos.y + dy };
          const oldX = (n?.x ?? 0);
          const oldY = (n?.y ?? 0);
          if (Math.abs(anchored.x - oldX) < epsilon && Math.abs(anchored.y - oldY) < epsilon) {
            return null;
          }
          return {
            nodeId,
            oldPosition: n ? { x: oldX, y: oldY } : undefined,
            newPosition: anchored,
          };
        })
        .filter((m): m is NonNullable<typeof m> => m !== null);
      
      // Ensure the newly created node is always included in moves if it has a layout position
      const createdNodePos = layout.nodePositions.get(this.createdShapeId!);
      if (createdNodePos) {
        const createdNodeInStore = store.nodes.find((n) => n.id === this.createdShapeId);
        if (createdNodeInStore) {
          const anchored = { x: createdNodePos.x + dx, y: createdNodePos.y + dy };
          const oldX = (createdNodeInStore?.x ?? 0);
          const oldY = (createdNodeInStore?.y ?? 0);
          const existingMove = moves.find((m) => m.nodeId === this.createdShapeId);
          if (!existingMove && (Math.abs(anchored.x - oldX) >= epsilon || Math.abs(anchored.y - oldY) >= epsilon)) {
            moves.push({
              nodeId: this.createdShapeId!,
              oldPosition: { x: oldX, y: oldY },
              newPosition: anchored,
            });
          }
        }
      }

      if (moves.length === 0) return;
      this.moveCmd = new MoveNodesCommand(moves);
      await this.moveCmd.execute();
      const postStore = useDiagramStore.getState();
      const movedPos = new Map(moves.map((m) => [m.nodeId, m.newPosition] as const));
      const movedIds = new Set(moves.map((m) => m.nodeId));

      this.edgeUpdateCommands = [];

      // (obsolete per exact algorithm) removed

      // (pair-based anchor selection below)
      const chooseAnchorPair = (aNode: any, bNode: any, aPos?: { x: number; y: number }, bPos?: { x: number; y: number }) =>
        chooseAnchorPairForStrategy(rectFromNode(aNode, aPos), rectFromNode(bNode, bPos), layoutStrategy);

      for (const e of postStore.edges) {
        if ((e as any)?.type !== 'sequence_flow') continue;
        const fromId = (e as any).from as string;
        const toId = (e as any).to as string;
        if (!fromId || !toId) continue;
        if (fromId.startsWith('__free') || toId.startsWith('__free')) continue;
        if (!movedIds.has(fromId) && !movedIds.has(toId)) continue;

        const fromNode = postStore.nodes.find((n) => n.id === fromId) as any;
        const toNode = postStore.nodes.find((n) => n.id === toId) as any;
        if (!fromNode || !toNode) continue;

        const fromPos = movedPos.get(fromId);
        const toPos = movedPos.get(toId);
        const endpoints = shiftedEdgeEndpoints.get((e as any).id as string);
        const pair = endpoints
          ? ({
              aSide: anchorSideFromPoint(rectFromNode(fromNode, fromPos), endpoints.start),
              bSide: anchorSideFromPoint(rectFromNode(toNode, toPos), endpoints.end),
            } as const)
          : chooseAnchorPair(fromNode, toNode, fromPos, toPos);

        const prevFromAnchor = (e as any).fromAnchor as any;
        const prevToAnchor = (e as any).toAnchor as any;
        const fromLocked = !!(e as any).fromAnchorLocked;
        const toLocked = !!(e as any).toAnchorLocked;

        const updates: Record<string, any> = {};
        if (!fromLocked) {
          if (prevFromAnchor !== pair.aSide) {
            updates.fromAnchor = pair.aSide as any;
            updates.fromOffset = 0.5 as any;
          }
        }
        if (!toLocked) {
          if (prevToAnchor !== pair.bSide) {
            updates.toAnchor = pair.bSide as any;
            updates.toOffset = 0.5 as any;
          }
        }

        if (Object.keys(updates).length === 0) continue;

        const cmd = new UpdateEdgeCommand(e.id, updates as any);
        cmd.execute();
        this.edgeUpdateCommands.push(cmd);
      }
    }
  }

  /**
   * Re-allocate `fromAnchor`/`toAnchor` on every flow edge that touches
   * the just-created shape, using the shape's *current* position. Runs
   * after the auto-dock RAF chain so anchor handles end up on the side
   * of the shape that actually faces the neighbour. Idempotent — if
   * sides already match, no edge gets touched.
   */
  private refreshEdgeAnchors(strategy: 'left-to-right' | 'top-to-bottom' | 'hierarchical' | 'compact'): void {
    if (!this.createdShapeId) return;
    const store = useDiagramStore.getState();
    const newShape = store.nodes.find((n) => n.id === this.createdShapeId);
    if (!newShape) return;

    for (const e of store.edges) {
      if ((e as any).type !== 'sequence_flow') continue;
      const fromId = (e as any).from as string | undefined;
      const toId = (e as any).to as string | undefined;
      if (!fromId || !toId) continue;
      if (fromId.startsWith('__free') || toId.startsWith('__free')) continue;
      if (fromId !== this.createdShapeId && toId !== this.createdShapeId) continue;

      const fromNode = store.nodes.find((n) => n.id === fromId);
      const toNode = store.nodes.find((n) => n.id === toId);
      if (!fromNode || !toNode) continue;

      const pair = chooseAnchorPairForStrategy(rectFromNode(fromNode), rectFromNode(toNode), strategy);
      if (!pair.aSide || !pair.bSide) continue;

      const updates: Record<string, unknown> = {};
      if (!(e as any).fromAnchorLocked && (e as any).fromAnchor !== pair.aSide) {
        updates.fromAnchor = pair.aSide;
        updates.fromOffset = 0.5;
      }
      if (!(e as any).toAnchorLocked && (e as any).toAnchor !== pair.bSide) {
        updates.toAnchor = pair.bSide;
        updates.toOffset = 0.5;
      }
      if (Object.keys(updates).length === 0) continue;

      const cmd = new UpdateEdgeCommand(e.id, updates as any);
      cmd.execute();
      this.edgeUpdateCommands.push(cmd);
    }
  }

  async undo(): Promise<void> {
    for (let i = this.edgeUpdateCommands.length - 1; i >= 0; i -= 1) {
      try { this.edgeUpdateCommands[i]!.undo(); } catch {}
    }
    if (this.moveCmd) {
      await this.moveCmd.undo();
    }
    if (this.edgeCmd && 'undo' in this.edgeCmd && typeof (this.edgeCmd as any).undo === 'function') {
      await (this.edgeCmd as any).undo();
    }
    await (this.shapeCmd as any).undo?.();
  }

  async redo(): Promise<void> {
    // Redo should restore the exact same node id and wiring
    // rather than re-executing with a fresh CreateShapeCommand.
    // 1) Redo the shape using the inner command so it preserves id
    // 2) Redo (or recreate) the connector
    // 3) Re-run the same post-create layout + anchor adjustment flow

    const storeBefore = useDiagramStore.getState();
    const preFlowchartNodesCount = storeBefore.nodes.filter((n: any) =>
      n?.type === 'process' || n?.type === 'decision' || n?.type === 'terminator' || n?.type === 'data' || n?.type === 'document' || n?.type === 'flowchart-shape'
    ).length;

    const mm = getNodeMutationManager();
    mm.runInTransaction(() => {
      // Redo the shape first so the id is restored
      (this.shapeCmd as any)?.redo?.();
      this.createdShapeId = (this.shapeCmd as any)?.getCreatedId?.();

      if (!this.createdShapeId) {
        // Fallback: if inner redo didn't restore, execute once
        (this.shapeCmd as any)?.execute?.();
        this.createdShapeId = (this.shapeCmd as any)?.getCreatedId?.();
      }

      if (!this.createdShapeId) {
        return;
      }

      const anchorsFor = (dir: 'top' | 'right' | 'bottom' | 'left'): { from: 'top' | 'right' | 'bottom' | 'left'; to: 'top' | 'right' | 'bottom' | 'left' } => {
        switch (dir) {
          case 'top': return { from: 'top', to: 'bottom' };
          case 'right': return { from: 'right', to: 'left' };
          case 'bottom': return { from: 'bottom', to: 'top' };
          case 'left': return { from: 'left', to: 'right' };
        }
      };

      // Redo the edge if we have it; else recreate it
      if (this.edgeCmd && 'redo' in this.edgeCmd && typeof (this.edgeCmd as any).redo === 'function') {
        (this.edgeCmd as any).redo();
      } else {
        const { from, to } = anchorsFor(this.placementDirection);
        this.edgeCmd = new CreateSequenceFlowCommand({
          from: this.params.sourceNodeId,
          to: this.createdShapeId!,
          fromAnchor: from,
          toAnchor: to,
          routingType: 'orthogonalRounded',
          label: this.branchLabel,
        });
        this.edgeCmd.execute();
      }
    });

    // Re-run the same post-create layout + anchor adjustment logic
    const layoutStore = useFlowchartLayoutStore.getState();
    if (layoutStore.autoLayoutMode === 'off') {
      if (this.moveCmd) {
        await this.moveCmd.redo();
        this.refreshEdgeAnchors(layoutStore.layoutStrategy);
      }
      return;
    }
    const layoutStrategy = layoutStore.layoutStrategy;

    const waitForSync = async () => {
      await new Promise<void>((resolve) => {
        if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(() => resolve());
        } else {
          setTimeout(() => resolve(), 0);
        }
      });
    };

    if (preFlowchartNodesCount <= 1) {
      await waitForSync();
    }
    await waitForSync();

    const store = useDiagramStore.getState();

    const layoutService = new ProcessLayoutService();
    const layoutOptions = {
      strategy: layoutStore.layoutStrategy,
      nodeSpacing: 80,
      rankSpacing: 100,
    };

    let layout: { nodePositions: Map<string, { x: number; y: number }>; edgeEndpoints: Map<string, { start: { x: number; y: number }; end: { x: number; y: number } }> } = {
      nodePositions: new Map(),
      edgeEndpoints: new Map(),
    };
    if (layoutStore.autoLayoutMode === 'full') {
      const seedIds = this.createdShapeId
        ? [this.params.sourceNodeId, this.createdShapeId]
        : [this.params.sourceNodeId];
      const connectedNodeIds = getConnectedFlowchartNodeIds(store.nodes, store.edges, seedIds);
      layout = await layoutService.incrementalLayout(store.nodes, store.edges, connectedNodeIds, layoutOptions);
    } else {
      const affectedNodeIds = this.createdShapeId
        ? [this.params.sourceNodeId, this.createdShapeId]
        : [this.params.sourceNodeId];
      layout = await layoutService.incrementalLayout(store.nodes, store.edges, affectedNodeIds, layoutOptions);
    }

    if (layout.nodePositions.size === 0) {
      const seedIds = this.createdShapeId
        ? [this.params.sourceNodeId, this.createdShapeId]
        : [this.params.sourceNodeId];
      const connectedNodeIds = getConnectedFlowchartNodeIds(store.nodes, store.edges, seedIds);
      layout = await layoutService.incrementalLayout(store.nodes, store.edges, connectedNodeIds, layoutOptions);
    }

    if (layout.nodePositions.size > 0) {
      const sourceBefore = storeBefore.nodes.find((n) => n.id === this.params.sourceNodeId);
      const sourceLayout = layout.nodePositions.get(this.params.sourceNodeId);
      const dx = sourceBefore && sourceLayout ? (sourceBefore.x ?? 0) - sourceLayout.x : 0;
      const dy = sourceBefore && sourceLayout ? (sourceBefore.y ?? 0) - sourceLayout.y : 0;

      const shiftedEdgeEndpoints = new Map<string, { start: { x: number; y: number }; end: { x: number; y: number } }>();
      for (const [edgeId, pts] of layout.edgeEndpoints.entries()) {
        shiftedEdgeEndpoints.set(edgeId, {
          start: { x: pts.start.x + dx, y: pts.start.y + dy },
          end: { x: pts.end.x + dx, y: pts.end.y + dy },
        });
      }

      const epsilon = 0.01;
      const moves = Array.from(layout.nodePositions.entries())
        .map(([nodeId, pos]) => {
          const n = store.nodes.find(nn => nn.id === nodeId);
          if (!n) return null;
          const anchored = { x: pos.x + dx, y: pos.y + dy };
          const oldX = (n?.x ?? 0);
          const oldY = (n?.y ?? 0);
          if (Math.abs(anchored.x - oldX) < epsilon && Math.abs(anchored.y - oldY) < epsilon) {
            return null;
          }
          return {
            nodeId,
            oldPosition: n ? { x: oldX, y: oldY } : undefined,
            newPosition: anchored,
          };
        })
        .filter((m): m is NonNullable<typeof m> => m !== null);

      const createdNodePos = this.createdShapeId ? layout.nodePositions.get(this.createdShapeId) : undefined;
      if (createdNodePos && this.createdShapeId) {
        const createdNodeInStore = store.nodes.find((n) => n.id === this.createdShapeId);
        if (createdNodeInStore) {
          const anchored = { x: createdNodePos.x + dx, y: createdNodePos.y + dy };
          const oldX = (createdNodeInStore?.x ?? 0);
          const oldY = (createdNodeInStore?.y ?? 0);
          const existingMove = moves.find((m) => m.nodeId === this.createdShapeId);
          if (!existingMove && (Math.abs(anchored.x - oldX) >= epsilon || Math.abs(anchored.y - oldY) >= epsilon)) {
            moves.push({
              nodeId: this.createdShapeId,
              oldPosition: { x: oldX, y: oldY },
              newPosition: anchored,
            });
          }
        }
      }

      if (moves.length > 0) {
        this.moveCmd = new MoveNodesCommand(moves);
        await this.moveCmd.execute();
      }

      const postStore = useDiagramStore.getState();
      const movedPos = new Map(moves.map((m) => [m.nodeId, m.newPosition] as const));
      const movedIds = new Set(moves.map((m) => m.nodeId));

      this.edgeUpdateCommands = [];
      const chooseAnchorPair = (aNode: any, bNode: any, aPos?: { x: number; y: number }, bPos?: { x: number; y: number }) =>
        chooseAnchorPairForStrategy(rectFromNode(aNode, aPos), rectFromNode(bNode, bPos), layoutStrategy);

      for (const e of postStore.edges) {
        if ((e as any)?.type !== 'sequence_flow') continue;
        const fromId = (e as any).from as string;
        const toId = (e as any).to as string;
        if (!fromId || !toId) continue;
        if (fromId.startsWith('__free') || toId.startsWith('__free')) continue;
        if (!movedIds.has(fromId) && !movedIds.has(toId)) continue;

        const fromNode = postStore.nodes.find((n) => n.id === fromId) as any;
        const toNode = postStore.nodes.find((n) => n.id === toId) as any;
        if (!fromNode || !toNode) continue;

        const fromPos = movedPos.get(fromId);
        const toPos = movedPos.get(toId);
        const endpoints = shiftedEdgeEndpoints.get((e as any).id as string);
        const pair = endpoints
          ? ({
              aSide: anchorSideFromPoint(rectFromNode(fromNode, fromPos), endpoints.start),
              bSide: anchorSideFromPoint(rectFromNode(toNode, toPos), endpoints.end),
            } as const)
          : chooseAnchorPair(fromNode, toNode, fromPos, toPos);
        const nextFromAnchor = { side: pair.aSide as any, offset: 0.5 };
        const nextToAnchor = { side: pair.bSide as any, offset: 0.5 };

        const prevFromAnchor = (e as any).fromAnchor as any;
        const prevToAnchor = (e as any).toAnchor as any;
        const prevFromOffset = (e as any).fromOffset as any;
        const prevToOffset = (e as any).toOffset as any;
        const fromLocked = !!(e as any).fromAnchorLocked;
        const toLocked = !!(e as any).toAnchorLocked;

        const updates: Record<string, any> = {};
        if (!fromLocked) {
          if (
            prevFromAnchor !== nextFromAnchor.side ||
            typeof prevFromOffset !== 'number' ||
            Math.abs((prevFromOffset as number) - nextFromAnchor.offset) > 1e-6
          ) {
            updates.fromAnchor = nextFromAnchor.side as any;
            updates.fromOffset = nextFromAnchor.offset as any;
          }
        }
        if (!toLocked) {
          if (
            prevToAnchor !== nextToAnchor.side ||
            typeof prevToOffset !== 'number' ||
            Math.abs((prevToOffset as number) - nextToAnchor.offset) > 1e-6
          ) {
            updates.toAnchor = nextToAnchor.side as any;
            updates.toOffset = nextToAnchor.offset as any;
          }
        }

        if (Object.keys(updates).length === 0) continue;

        const cmd = new UpdateEdgeCommand(e.id, updates as any);
        cmd.execute();
        this.edgeUpdateCommands.push(cmd);
      }
    }
  }

  canExecute(): boolean {
    return this.shapeCmd.canExecute ? this.shapeCmd.canExecute() : true;
  }

  canUndo(): boolean {
    return true;
  }

  /**
   * Get the ID of the created shape (available after execution)
   */
  getCreatedShapeId(): string | undefined {
    return this.createdShapeId;
  }

  toScenarioDelta(): CommandDeltaContribution {
    return {
      additions: [this.scenarioAddition],
      edgeAdditions: [this.scenarioEdgeAddition],
    };
  }

  getUndoDelta(): CommandDeltaContribution {
    return {
      removals: [
        {
          nodeId: this.scenarioNodeId,
          originalData: {
            name: this.scenarioAddition.name,
            type: this.scenarioAddition.type,
          },
        },
      ],
      edgeRemovals: [{ edgeId: this.scenarioEdgeId }],
    };
  }

}
